import type { ICustomerData } from '@/models/customer'
import type {
  ICustomerService,
  ICustomerPaginatedListData,
  ICustomerFilterParam
} from './customer.types'
import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import axios from '@/axios'

/**
 * A class representing an CustomerService API handler.
 */
export class CustomerService implements ICustomerService {
  private endpoint: string

  // CustomerService constructor
  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/customers/'
  }

  // Method to fetch a list of customers from the API
  fetchCustomers(filter?: ICustomerFilterParam): Promise<ICustomerPaginatedListData> {
    const config: AxiosRequestConfig | undefined = filter ? { params: filter } : undefined
    return this.axios.get(`${this.endpoint}`, config).then(({ data }) => data)
  }

  // Method to fetch a single customer by their ID from the API
  fetchCustomer(id: number): Promise<ICustomerData> {
    return this.axios.get(`${this.endpoint}${id}/`).then(({ data }) => data)
  }

  // Method to create a new customer on the API
  createCustomer(data: ICustomerData): Promise<ICustomerData> {
    return this.axios.post(`${this.endpoint}`, data).then(({ data }) => data)
  }

  // Method to update an existing customer on the API
  updateCustomer(data: ICustomerData): Promise<ICustomerData> {
    return this.axios.patch(`${this.endpoint}${data.id}/`, data).then(({ data }) => data)
  }

  // Method to delete a customer by their ID from the API
  deleteCustomer(id: number): Promise<void> {
    return this.axios.delete(`${this.endpoint}${id}/`).then(({ data }) => data)
  }

  /** Method to update a single customer's sites list by their ID from the API */
  setCustomerSites(id: number, sites: number[]): Promise<ICustomerData> {
    return this.axios
      .put(`${this.endpoint}${id}/set-sites/`, {
        sites: sites
      })
      .then(({ data }) => data)
  }
}

// Default export instantiation of the CustomerService
const customerService = new CustomerService(axios)
export default customerService
